import { Handshake, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import {
  type TechPartnerMapping,
  useDeleteTechPartner,
  useTechPartners,
} from "@/hooks/use-tech-partners";
import { CreateTechPartnerDialog } from "./CreateTechPartnerDialog";
import { EditTechPartnerDialog } from "./EditTechPartnerDialog";

type DialogState =
  | { type: "none" }
  | { type: "create" }
  | { type: "edit"; mapping: TechPartnerMapping }
  | { type: "delete"; mapping: TechPartnerMapping };

export function TechPartnersTable() {
  const [dialog, setDialog] = useState<DialogState>({ type: "none" });
  const { data: mappings = [], isLoading, isError, error, refetch } = useTechPartners();
  const remove = useDeleteTechPartner();

  function closeDialog() {
    setDialog({ type: "none" });
  }

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load tech partners"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground max-w-prose">
          Pair products with the tech partners responsible for them.
        </p>
        <Button onClick={() => setDialog({ type: "create" })} className="shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          Add Pairing
        </Button>
      </div>

      <DataTable
        id="tech-partners"
        headers={[
          { key: "product", label: "Product", defaultWidth: 220 },
          { key: "partner", label: "Tech Partner", defaultWidth: 220 },
          { key: "email", label: "Email", defaultWidth: 260 },
          { key: "actions", label: "Actions", className: "text-right", defaultWidth: 110 },
        ]}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Handshake}
            title="No pairings"
            description="Pair a product with a tech partner to get started."
          />
        }
      >
        {mappings.map((mapping) => (
          <tr
            key={mapping.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3 font-medium">{mapping.product}</td>
            <td className="px-4 py-3">{mapping.partnerName}</td>
            <td className="px-4 py-3 text-muted-foreground">{mapping.partnerEmail ?? "—"}</td>
            <td className="px-4 py-3 text-right">
              <div className="flex items-center justify-end gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setDialog({ type: "edit", mapping })}
                >
                  <Pencil className="h-4 w-4" />
                  <span className="sr-only">Edit</span>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setDialog({ type: "delete", mapping })}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="sr-only">Delete</span>
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </DataTable>

      <CreateTechPartnerDialog
        open={dialog.type === "create"}
        onOpenChange={(open) => !open && closeDialog()}
        takenProducts={mappings.map((m) => m.product)}
      />

      {dialog.type === "edit" && (
        <EditTechPartnerDialog
          open
          onOpenChange={(open) => !open && closeDialog()}
          mapping={dialog.mapping}
          takenProducts={mappings.filter((m) => m.id !== dialog.mapping.id).map((m) => m.product)}
        />
      )}

      {dialog.type === "delete" && (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && closeDialog()}
          title="Delete Pairing"
          description={`Are you sure you want to remove the pairing between "${dialog.mapping.product}" and "${dialog.mapping.partnerName}"? This cannot be undone.`}
          confirmLabel="Delete"
          variant="destructive"
          loading={remove.isPending}
          onConfirm={async () => {
            await remove.mutateAsync(dialog.mapping.id);
            closeDialog();
          }}
        />
      )}
    </div>
  );
}
